// Identity & key management (ARCHITECTURE.md §3, §5, §11).
//
// Model: the BIP39 mnemonic is the ROOT secret and the only backup. From its
// seed we deterministically derive two SEPARATE P-256 keypairs — one for
// signing (ECDSA), one for key-agreement (ECDH). The private keys are imported
// into WebCrypto as NON-EXTRACTABLE CryptoKeys; the raw scalars are then
// dropped. All runtime signing/encryption goes through WebCrypto, so the
// private key material is never readable by application code again.
//
// @noble is used ONLY here, at derive/recover time, to turn the seed into a
// keypair and to (de)compress public points — operations WebCrypto cannot do
// on raw scalars. The secret it touches is derived from the user's own seed.

import { p256 } from '@noble/curves/p256';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha2';
import { generateMnemonic as bip39Generate, mnemonicToSeed, validateMnemonic as bip39Validate } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import { base64urlToBytes, bytesToBase64url, concatBytes } from './encoding';

const subtle = globalThis.crypto.subtle;

const CIRCLE_ID_VERSION = 0x01;
const HKDF_SALT = new TextEncoder().encode('circle/v1');
const INFO_SIGN = new TextEncoder().encode('circle/v1/sign/p256');
const INFO_ECDH = new TextEncoder().encode('circle/v1/ecdh/p256');

/** Runtime keys for the local user. Private keys are non-extractable. */
export interface KeyBundle {
  signPrivate: CryptoKey; // ECDSA P-256, non-extractable, ['sign']
  signPublicRaw: Uint8Array; // 33-byte compressed point
  ecdhPrivate: CryptoKey; // ECDH P-256, non-extractable, ['deriveBits']
  ecdhPublicRaw: Uint8Array; // 33-byte compressed point
}

export interface Identity {
  circleId: string; // base64url public identifier (shareable)
  keys: KeyBundle;
}

// --- mnemonic ---------------------------------------------------------------

/** Generate a fresh recovery phrase. 128 bits => 12 words, 256 => 24 words. */
export function generateMnemonic(strengthBits: 128 | 256 = 128): string {
  return bip39Generate(wordlist, strengthBits);
}

export function validateMnemonic(mnemonic: string): boolean {
  return bip39Validate(mnemonic.trim().replace(/\s+/g, ' '), wordlist);
}

// --- deterministic derivation ----------------------------------------------

/**
 * Derive a valid P-256 private scalar from the seed for a given purpose.
 * HKDF-expand with a counter, rejecting scalars outside [1, n-1].
 */
function deriveScalar(seed: Uint8Array, info: Uint8Array): Uint8Array {
  const n = p256.CURVE.n;
  for (let counter = 0; counter < 256; counter++) {
    const material = hkdf(sha256, seed, HKDF_SALT, concatBytes(info, Uint8Array.of(counter)), 32);
    let value = 0n;
    for (const byte of material) value = (value << 8n) | BigInt(byte);
    if (value >= 1n && value < n) return material;
  }
  throw new Error('Failed to derive a valid key scalar');
}

/** Build a WebCrypto JWK from a P-256 private scalar and its public point. */
function jwkFromScalar(scalar: Uint8Array): { d: string; x: string; y: string } {
  const uncompressed = p256.getPublicKey(scalar, false); // 0x04 || x(32) || y(32)
  return {
    d: bytesToBase64url(scalar),
    x: bytesToBase64url(uncompressed.slice(1, 33)),
    y: bytesToBase64url(uncompressed.slice(33, 65)),
  };
}

async function importSignPrivate(scalar: Uint8Array): Promise<CryptoKey> {
  const { d, x, y } = jwkFromScalar(scalar);
  return subtle.importKey(
    'jwk',
    { kty: 'EC', crv: 'P-256', d, x, y, ext: false },
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['sign'],
  );
}

async function importEcdhPrivate(scalar: Uint8Array): Promise<CryptoKey> {
  const { d, x, y } = jwkFromScalar(scalar);
  return subtle.importKey(
    'jwk',
    { kty: 'EC', crv: 'P-256', d, x, y, ext: false },
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits'],
  );
}

/**
 * Derive the full identity from a recovery phrase. Deterministic: the same
 * phrase always yields the same Circle ID and keys.
 */
export async function deriveIdentity(mnemonic: string, passphrase = ''): Promise<Identity> {
  const seed = await mnemonicToSeed(mnemonic.trim().replace(/\s+/g, ' '), passphrase);

  const signScalar = deriveScalar(seed, INFO_SIGN);
  const ecdhScalar = deriveScalar(seed, INFO_ECDH);

  const [signPrivate, ecdhPrivate] = await Promise.all([
    importSignPrivate(signScalar),
    importEcdhPrivate(ecdhScalar),
  ]);

  const signPublicRaw = p256.getPublicKey(signScalar, true); // compressed 33B
  const ecdhPublicRaw = p256.getPublicKey(ecdhScalar, true);

  // Best-effort zeroing of derived scalar material.
  signScalar.fill(0);
  ecdhScalar.fill(0);
  seed.fill(0);

  const keys: KeyBundle = { signPrivate, signPublicRaw, ecdhPrivate, ecdhPublicRaw };
  return { circleId: encodeCircleId(signPublicRaw, ecdhPublicRaw), keys };
}

// --- Circle ID --------------------------------------------------------------

/** Encode the two public keys into a single shareable identifier. */
export function encodeCircleId(signPublicRaw: Uint8Array, ecdhPublicRaw: Uint8Array): string {
  return bytesToBase64url(concatBytes(Uint8Array.of(CIRCLE_ID_VERSION), signPublicRaw, ecdhPublicRaw));
}

export interface DecodedCircleId {
  signPublicRaw: Uint8Array; // 33-byte compressed
  ecdhPublicRaw: Uint8Array; // 33-byte compressed
}

export function decodeCircleId(circleId: string): DecodedCircleId {
  const bytes = base64urlToBytes(circleId.trim());
  if (bytes.length !== 1 + 33 + 33 || bytes[0] !== CIRCLE_ID_VERSION) {
    throw new Error('Invalid Circle ID');
  }
  return {
    signPublicRaw: bytes.slice(1, 34),
    ecdhPublicRaw: bytes.slice(34, 67),
  };
}

// --- public-key imports for verifying / encrypting to others ----------------

/** Decompress a 33-byte point to the 65-byte form WebCrypto's 'raw' expects. */
function decompress(compressed: Uint8Array): Uint8Array {
  return p256.Point.fromHex(compressed).toRawBytes(false);
}

/** Import someone's signing public key to verify their signatures. */
export function importVerifyKey(signPublicRaw: Uint8Array): Promise<CryptoKey> {
  return subtle.importKey(
    'raw',
    decompress(signPublicRaw),
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['verify'],
  );
}

/** Import someone's ECDH public key to derive a shared secret with them. */
export function importEcdhPublic(ecdhPublicRaw: Uint8Array): Promise<CryptoKey> {
  return subtle.importKey(
    'raw',
    decompress(ecdhPublicRaw),
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
}
